import PackageContext from '@/contexts/PackageContext';
import { FormsFeatureContext } from '@ngt/forms';
import { useScopedField } from '@ngt/forms-core';
import { useContext, useEffect, useRef, useState } from 'react';
import * as Dtos from '../api/dtos';
import { RegistrationSession } from '../api/dtos';

const subscription = { value: true };

export const useVoucherPreselection = (code?: string) => {
    
    const { context: { setValue: setPackageId } } = useScopedField<number>('packageId', subscription);
    const { context: { setValue: setSessions } } = useScopedField<RegistrationSession[]>('sessions', subscription);
    const { context: { setValue: setRegistrationType } } = useScopedField<number>('registrationType', subscription);
    const { context: { setValue: setVoucherId } } = useScopedField<number>('voucherId', subscription);
    const { context: { setValue: setTrialCoordinator } } = useScopedField<boolean>('trialCoordinator', subscription);
    const { context: { setValue: setStudent } } = useScopedField<boolean>('student', subscription);
    const { context: { setValue: setRegistrar } } = useScopedField<boolean>('registrar', subscription);
    const { context: { setValue: setStaff } } = useScopedField<boolean>('staff', subscription);
    const { context: { setValue: setDefaultUser } } = useScopedField<boolean>('defaultUser', subscription);
    const { context: { setValue: setAttendeeFromJapanOrKorea } } = useScopedField<boolean>('attendeeFromJapanOrKorea', subscription);
    
    const {state: {value: packageId}} = useScopedField<number>('packageId', {value: true});
    const { serviceStackClient } = useContext(FormsFeatureContext);
    const { additionalSessions, additionalSessionsLoading } = useContext(PackageContext);

    const [selectAllAdditionalSessions, setSelectAllAdditionalSessions] = useState(false);
    const loadedCodeRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        
        // Don't load if no code provided
        if (!code) {
            return;
        }

        // Don't load if we already loaded this code
        if (loadedCodeRef.current === code) {
            return;
        }

        // Don't load if packageId is already set (user might have manually selected)
        if (packageId) {
            return;
        }

        const loadVoucherPreselection = async () => {
            try {
                
                const response = await serviceStackClient.get(
                    new Dtos.GetVoucherPreselectedPackage({ code })
                );


                // If the voucher is already redeemed, don't preselect anything.
                if (response.redeemed === true) {
                    loadedCodeRef.current = code;
                    return;
                }

                // Mark this code as loaded before setting values to prevent re-triggering
                loadedCodeRef.current = code;

                if ((response as any).voucherId != null) {
                    setVoucherId((response as any).voucherId, true, true, true, true);
                }
                else if ((response as any).voucherid != null) {
                    setVoucherId((response as any).voucherid, true, true, true, true);
                }

                if (response.packageId != null) {
                    setPackageId(response.packageId, true, true, true, true);
                }
                
                const selectedSessions = (response.preselectedPackageSessionIds ?? []).map(
                    (sessionId: number) => new Dtos.RegistrationSession({ sessionId })
                );


                if (response.preselectedPackageSessionIds != null && response.preselectedPackageSessionIds.length > 0) {
                    setSessions(selectedSessions, true, true, true, true);
                }

                if (response.registrationType != null) {
                    setRegistrationType(response.registrationType, true, true, true, true);
                    
                    // Also set the corresponding boolean field so RegistrationTypeUpdater doesn't override it
                    // RegistrationType enum: 1=DefaultUser, 2=Student, 3=TrialCoordinator, 4=Registrar, 5=Staff, 6=JapanOrKorea
                    switch (response.registrationType) {
                        case 1: // DefaultUser
                            setDefaultUser(true, true, true, true, true);
                            break;
                        case 2: // Student
                            setStudent(true, true, true, true, true);
                            break;
                        case 3: // TrialCoordinator
                            setTrialCoordinator(true, true, true, true, true);
                            break;
                        case 4: // Registrar
                            setRegistrar(true, true, true, true, true);
                            break;
                        case 5: // Staff
                            setStaff(true, true, true, true, true);
                            break;
                        case 6: // JapanOrKorea
                            setAttendeeFromJapanOrKorea(true, true, true, true, true);
                            break;
                    }
                    
                }
                
            } catch (error) {
                throw error;
            }
        };

        loadVoucherPreselection();
    }, [code, packageId, serviceStackClient, setPackageId, setVoucherId, setRegistrationType, setSessions, setTrialCoordinator, setStudent, setRegistrar, setStaff, setDefaultUser, setAttendeeFromJapanOrKorea]);

    useEffect(() => {
        if (!selectAllAdditionalSessions || additionalSessionsLoading) {
            return;
        }
        setSessions(current => {
            const merged = [...(current ?? [])];
            const existingSessionIds = new Set(merged.map(x => x.sessionId));

            for (const session of additionalSessions ?? []) {
                if (session.id == null || existingSessionIds.has(session.id)) {
                    continue;
                }

                merged.push(new Dtos.RegistrationSession({ sessionId: session.id }));
                existingSessionIds.add(session.id);
            }

            return merged;
        }, true, true, true, false);
    
    }, [selectAllAdditionalSessions, additionalSessionsLoading, additionalSessions, setSessions]);
};

export default useVoucherPreselection;
